import React, { useState } from "react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";
import { validateOpenAiProjectKeys } from "../utils/openAiConnectorValidation";

/**
 * Generic GRC-connector panel for AI provider admin keys (OpenAI / Anthropic).
 * These connectors feed AI Intelligence discovery only — their "Test" runs a lightweight
 * validation call server-side (no GRC controls are generated). Extracted to its own file per
 * the frontend guideline: don't grow IntegrationSettings further.
 *
 * Props:
 *  - sourceSystem: "OPENAI" | "ANTHROPIC"
 *  - label: display name ("OpenAI" / "Anthropic")
 *  - keyField: DTO field name for the admin key (openAiAdminKey / anthropicAdminKey)
 *  - keyLabel / keyPlaceholder / helpText: form copy
 *  - connectors: current connector list for this source (parent-fetched)
 *  - onChanged: refetch callback after create/update/delete/test
 *  - canManage: RBAC gate
 *  - requestConfirmAsync: shared typed-confirm modal from IntegrationSettings
 *  - extraKeys (optional, OpenAI only): { field, label, placeholder, help, clearLabel } — a second,
 *    multi-line credential (OpenAI project application keys, one per line). Rendered ONLY when the
 *    caller supplies it, so the Anthropic card is unchanged. Write-only like the admin key: the
 *    box always opens empty and blank text OMITS `field` from the payload (server keeps the stored
 *    keys). Clearing is the separate, confirmed `clearLabel` control, which sends `[]`.
 *    `help` is a string or an array of paragraphs.
 *    Two optional members, both absent on the Anthropic card (absent = the behaviour above exactly):
 *    - `modeField`: DTO field for the save mode ("openAiProjectKeysMode"). On edit, a radio group
 *      chooses "add" (default, reset every time the form opens) or "replace"; create always sends
 *      "replace". The mode is sent only when keys are sent; the clear control never sends it.
 *    - `countField`: read-DTO field for the saved-key count ("openAiProjectKeyCount"), shown on
 *      each connector row. A missing count reads "not counted", never 0.
 *  - scalarFields (optional): a list of single-value credential fields beyond the admin key, each
 *    { key, label, kind, placeholder, help, configuredField, configuredLabels, clearLabel,
 *      clearConfirm, validate }. Rendered ONLY when supplied (absent = the panel exactly as before).
 *    - `key`: the DTO property, sent on save and named in `clearCredentialFields` on a clear.
 *    - `kind`: "secret" | "text" | "date".
 *      · secret: write-only. The input always opens empty (type=password) and the stored value is
 *        never read. Blank (or whitespace) OMITS `key` from the payload, so the server keeps the
 *        stored secret. Removing it is the separate, confirmed `clearLabel` control, shown on edit
 *        only while `connector[configuredField]` is true; it sends
 *        `{ …, [keyField]: null, clearCredentialFields: [key] }` and nothing else.
 *        `configuredField` names the read-DTO boolean (never the value); each connector row shows
 *        `configuredLabels.yes` / `.no` from it.
 *      · text / date: not secret. On edit the input is prefilled from `connector[key]`. A value is
 *        sent only when non-blank and different from the saved one (trimmed); emptying a saved value
 *        sends `key` in `clearCredentialFields` instead. A date is yyyy-MM-dd and may not be after
 *        today (UTC, matching the server).
 *    - `validate(value)` (optional): returns an error string or null, run on a non-blank value
 *      before saving.
 *    - `help`: a string shown under the field.
 *  - unavailable (optional): the source cannot be reached on this installation (Restricted /
 *    AirGapped profile). Add, Save (new connector) and Test are disabled; Edit, Update and Remove
 *    stay enabled because they only change saved data.
 */
// Absent is not zero: a connector saved before the count existed has no count.
const projectKeyCountText = (n) => {
  if (n === null || n === undefined) return "Saved project keys: not counted";
  if (n === 0) return "No project keys saved";
  if (n === 1) return "1 project key saved";
  return `${n} project keys saved`;
};

const rejectPastedSecret = (value) => (/^sk-ant-/i.test(value)
  ? "A key id identifies a key; it is not the key itself. Enter the key's id from the Anthropic Console, not a value starting sk-ant-."
  : null);

/**
 * The Anthropic card's compliance credential set (per connector): the compliance key (secret), its
 * key id, the admin key's id, and the date the Compliance API was enabled.
 */
export const ANTHROPIC_COMPLIANCE_FIELDS = [
  {
    key: "anthropicComplianceKey",
    label: "Compliance API Key",
    kind: "secret",
    placeholder: "sk-ant-admin01-…",
    help: "Used only to read the Compliance API Activity Feed. The key must be minted after the Compliance API was enabled for this Anthropic organization: an existing admin key will not work, even the one above, because a key created before enablement cannot read the feed. Mint a new key rather than troubleshooting an older one.",
    configuredField: "anthropicComplianceKeyConfigured",
    configuredLabels: { yes: "Compliance key configured", no: "No compliance key" },
    clearLabel: "Remove compliance key",
    clearConfirm: "Remove the saved compliance key? The admin key stays and inventory discovery is unaffected. The Activity Feed cannot be read again until you supply a new compliance key.",
  },
  {
    key: "anthropicComplianceKeyId",
    label: "Compliance API Key ID",
    kind: "text",
    placeholder: "apikey_…",
    help: "The id the Anthropic Console shows for the compliance key, not the key itself. Axaris uses it to leave its own feed reads out of the activity it reports.",
    validate: rejectPastedSecret,
  },
  {
    key: "anthropicAdminKeyId",
    label: "Admin API Key ID",
    kind: "text",
    placeholder: "apikey_…",
    help: "The id the Anthropic Console shows for the admin key above, not the key itself. Axaris uses it to leave its own inventory and usage reads out of the activity it reports.",
    validate: rejectPastedSecret,
  },
  {
    key: "anthropicComplianceEnabledAt",
    label: "Compliance API enabled on",
    kind: "date",
    help: "The date the Compliance API began recording for this Anthropic organization. Anthropic does not backfill, so nothing before this date can be read.",
  },
];

// Today's date as yyyy-MM-dd in UTC: the server rejects a date after the UTC calendar date.
const utcToday = () => new Date().toISOString().slice(0, 10);

const emptyScalarValues = (fields) => Object.fromEntries((fields || []).map(f => [f.key, ""]));

// Secrets always open empty; text and date open with the connector's saved value.
const scalarValuesFor = (fields, connector) => Object.fromEntries((fields || []).map(f =>
  [f.key, f.kind === "secret" ? "" : (connector?.[f.key] ?? "")]));

/**
 * The save half of the scalar-field contract. Returns { error } or { body, clear }: `body` holds
 * the values to send, `clear` the keys to clear.
 */
export const buildScalarFieldPayload = (fields, values, connector) => {
  const body = {};
  const clear = [];
  for (const f of fields || []) {
    const value = (values[f.key] ?? "").trim();
    if (f.kind === "secret") {
      if (value) {
        const error = f.validate?.(value);
        if (error) return { error };
        body[f.key] = value;
      }
      continue;
    }
    const saved = (connector?.[f.key] ?? "").toString().trim();
    if (!value) {
      if (saved) clear.push(f.key);
      continue;
    }
    if (f.kind === "date") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return { error: `${f.label} must be a date.` };
      if (value > utcToday()) return { error: `${f.label} cannot be in the future.` };
    }
    const error = f.validate?.(value);
    if (error) return { error };
    if (value !== saved) body[f.key] = value;
  }
  return { body, clear };
};

export default function AiProviderConnectorPanel({
  sourceSystem, label, keyField, keyLabel, keyPlaceholder, helpText,
  connectors = [], onChanged, canManage, requestConfirmAsync, extraKeys, scalarFields, unavailable = false,
}) {
  const apiCall = useApi();
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [extraKeysText, setExtraKeysText] = useState("");
  const [keysMode, setKeysMode] = useState("add");
  const [scalarValues, setScalarValues] = useState(() => emptyScalarValues(scalarFields));
  const [saving, setSaving] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());

  const withBusy = async (id, fn) => {
    setBusyIds(prev => new Set(prev).add(id));
    try { await fn(); }
    finally { setBusyIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const save = async () => {
    if (!displayName || (!adminKey && !editId))
      return alert(`Display name and ${keyLabel} are required.`);
    // Blank means keep: an empty box never sends [] (that would erase the stored keys on an
    // unrelated edit). Only the explicit clear control below sends [].
    let extraKeyList = [];
    if (extraKeys) {
      const check = validateOpenAiProjectKeys(extraKeysText);
      if (!check.ok) return alert(check.error);
      extraKeyList = check.keys;
    }
    let scalar = { body: {}, clear: [] };
    if (scalarFields) {
      scalar = buildScalarFieldPayload(scalarFields, scalarValues, editId ? connectors.find(c => c.id === editId) : null);
      if (scalar.error) return alert(scalar.error);
    }
    setSaving(true);
    try {
      const body = { sourceSystem, displayName, isActive: true, [keyField]: adminKey || null, ...scalar.body };
      if (scalar.clear.length > 0) body.clearCredentialFields = scalar.clear;
      if (extraKeys && extraKeyList.length > 0) {
        body[extraKeys.field] = extraKeyList;
        // Nothing is saved yet on create, so add and replace are the same: send "replace".
        if (extraKeys.modeField) body[extraKeys.modeField] = editId ? keysMode : "replace";
      }
      if (editId) await apiCall("PUT", `/grc-connectors/${editId}`, body);
      else        await apiCall("POST", "/grc-connectors", body);
      setFormOpen(false); setEditId(null); setDisplayName(""); setAdminKey(""); setExtraKeysText(""); setKeysMode("add");
      setScalarValues(emptyScalarValues(scalarFields));
      await onChanged?.();
    } catch (err) { alert(err?.response?.data?.message || `Failed to save ${label} connector.`); }
    finally { setSaving(false); }
  };

  // Explicit clear: the only path that sends an empty list. Keeps the admin key (null = unchanged).
  const clearExtraKeys = async () => {
    const connector = connectors.find(c => c.id === editId);
    if (!extraKeys || !connector) return;
    if (!(await requestConfirmAsync(extraKeys.clearLabel,
      `Remove every ${extraKeys.label.toLowerCase()} saved on ${connector.displayName}? The ${label} ` +
      `admin key stays. Discovery will stop seeing agents until you supply keys again, and the ` +
      `agents found through those keys leave the inventory on the next scan.`)))
      return;
    setSaving(true);
    try {
      await apiCall("PUT", `/grc-connectors/${editId}`, {
        sourceSystem, displayName: connector.displayName, isActive: connector.isActive ?? true,
        [keyField]: null, [extraKeys.field]: [],
      });
      setFormOpen(false); setEditId(null); setDisplayName(""); setAdminKey(""); setExtraKeysText(""); setKeysMode("add");
      await onChanged?.();
    } catch (err) { alert(err?.response?.data?.message || `Failed to remove ${extraKeys.label.toLowerCase()}.`); }
    finally { setSaving(false); }
  };

  // Explicit clear of one scalar secret: the only path that removes it. Keeps the admin key
  // (null = unchanged) and every other field.
  const clearScalarSecret = async (field) => {
    const connector = connectors.find(c => c.id === editId);
    if (!connector) return;
    if (!(await requestConfirmAsync(field.clearLabel,
      field.clearConfirm || `Remove the ${field.label.toLowerCase()} saved on ${connector.displayName}? The ${label} admin key stays.`)))
      return;
    setSaving(true);
    try {
      await apiCall("PUT", `/grc-connectors/${editId}`, {
        sourceSystem, displayName: connector.displayName, isActive: connector.isActive ?? true,
        [keyField]: null, clearCredentialFields: [field.key],
      });
      setFormOpen(false); setEditId(null); setDisplayName(""); setAdminKey(""); setExtraKeysText(""); setKeysMode("add");
      setScalarValues(emptyScalarValues(scalarFields));
      await onChanged?.();
    } catch (err) { alert(err?.response?.data?.message || `Failed to remove the ${field.label.toLowerCase()}.`); }
    finally { setSaving(false); }
  };

  const editingConnector = editId ? connectors.find(c => c.id === editId) : null;

  const test = (id) => withBusy(id, async () => {
    try { await apiCall("POST", `/grc-connectors/${id}/sync`); }
    catch (err) { alert(err?.response?.data?.message || `${label} key validation failed.`); }
    await onChanged?.();
  });

  const remove = async (id) => {
    if (!(await requestConfirmAsync(`Remove ${label} Connector`,
      `Are you sure you want to remove the ${label} connector? AI discovery will stop scanning this ` +
      `${label} organization; its discovered assets go stale and their risks close on the next cycle.`)))
      return;
    await withBusy(id, async () => {
      try { await apiCall("DELETE", `/grc-connectors/${id}`); }
      catch { alert(`Failed to delete ${label} connector.`); }
      await onChanged?.();
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">{label} Organization Connector</span>
        {canManage && (
          <button disabled={unavailable}
            onClick={() => { setEditId(null); setDisplayName(""); setAdminKey(""); setExtraKeysText(""); setKeysMode("add"); setScalarValues(emptyScalarValues(scalarFields)); setFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add {label} Connector
          </button>
        )}
      </div>

      {formOpen && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
            <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder={`e.g. Acme ${label}`}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {keyLabel} {editId ? <span className="text-gray-400">(leave blank to keep the saved key)</span> : <span className="text-red-500">*</span>}
            </label>
            <input type="password" value={adminKey} onChange={e => setAdminKey(e.target.value)} placeholder={keyPlaceholder}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
          </div>
          {extraKeys && (
            <div>
              <label htmlFor={`${sourceSystem}-extra-keys`} className="block text-xs font-medium text-gray-600 mb-1">
                {extraKeys.label} <span className="text-gray-400">(one per line, optional)</span>
              </label>
              <textarea id={`${sourceSystem}-extra-keys`} value={extraKeysText} onChange={e => setExtraKeysText(e.target.value)}
                placeholder={extraKeys.placeholder} rows={3} autoComplete="off" spellCheck={false}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
              <p className="text-[11px] text-gray-400 mt-1">
                {!extraKeys.modeField
                  ? "Saved keys are never shown. Leaving this box empty keeps the keys already saved; keys you enter replace them."
                  : keysMode === "replace"
                    ? "Saved keys are never shown. Keys you enter replace every saved key — include every key you want to keep. Leaving this box empty keeps the saved keys."
                    : "Saved keys are never shown. Keys you enter are added to the ones already saved. Leaving this box empty keeps the saved keys."}
              </p>
              {extraKeys.modeField && editId && (
                <div role="radiogroup" aria-label={`How to save ${extraKeys.label.toLowerCase()}`} className="flex items-center gap-4 mt-1.5">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input type="radio" name={`${sourceSystem}-keys-mode`} value="add"
                      checked={keysMode === "add"} onChange={() => setKeysMode("add")} />
                    Add to saved keys
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input type="radio" name={`${sourceSystem}-keys-mode`} value="replace"
                      checked={keysMode === "replace"} onChange={() => setKeysMode("replace")} />
                    Replace all saved keys
                  </label>
                </div>
              )}
            </div>
          )}
          {(scalarFields || []).map(f => {
            const id = `${sourceSystem}-${f.key}`;
            const configured = f.kind === "secret" && !!editingConnector?.[f.configuredField];
            return (
              <div key={f.key}>
                <label htmlFor={id} className="block text-xs font-medium text-gray-600 mb-1">
                  {f.label}{" "}
                  <span className="text-gray-400">
                    {configured ? "(a key is saved; leave blank to keep it)" : "(optional)"}
                  </span>
                </label>
                <input id={id}
                  type={f.kind === "secret" ? "password" : f.kind === "date" ? "date" : "text"}
                  autoComplete={f.kind === "secret" ? "new-password" : "off"} spellCheck={false}
                  max={f.kind === "date" ? utcToday() : undefined}
                  value={scalarValues[f.key] ?? ""}
                  onChange={e => { const v = e.target.value; setScalarValues(prev => ({ ...prev, [f.key]: v })); }}
                  placeholder={f.placeholder}
                  className={`w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500${f.kind === "date" ? "" : " font-mono"}`} />
                {f.help && <p className="text-[11px] text-gray-500 mt-1">{f.help}</p>}
                {f.kind === "secret" && f.clearLabel && configured && (
                  <button type="button" onClick={() => clearScalarSecret(f)} disabled={saving}
                    className="mt-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold disabled:opacity-40 transition-all">
                    {f.clearLabel}
                  </button>
                )}
              </div>
            );
          })}
          <button onClick={save} disabled={saving || (unavailable && !editId)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-40 transition-all">
            {saving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : (editId ? "Update Connector" : "Save Connector")}
          </button>
          {extraKeys && editId && (
            <button onClick={clearExtraKeys} disabled={saving}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold disabled:opacity-40 transition-all">
              {extraKeys.clearLabel}
            </button>
          )}
        </div>
      )}

      {connectors.length === 0 && !formOpen && (
        <p className="text-xs text-gray-400">No {label} connectors yet.</p>
      )}
      {connectors.map(c => (
        <div key={c.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
          <div>
            <div className="text-xs font-semibold text-gray-700">{c.displayName}</div>
            <div className="text-[11px] text-gray-400">
              {c.status === "CONNECTED" ? <span className="text-green-600 font-medium">Connected</span>
                : c.status === "ERROR" ? <span className="text-red-500 font-medium" title={c.syncError || ""}>Error — key invalid?</span>
                : <span>Pending validation</span>}
              {c.lastSyncAt && <span> · last checked {formatUtcDateTime(c.lastSyncAt)}</span>}
              {extraKeys?.countField && <span> · {projectKeyCountText(c[extraKeys.countField])}</span>}
              {(scalarFields || []).filter(f => f.kind === "secret" && f.configuredField && f.configuredLabels).map(f => (
                <span key={f.key}> · {c[f.configuredField] ? f.configuredLabels.yes : f.configuredLabels.no}</span>
              ))}
            </div>
          </div>
          {canManage && (
            <div className="flex items-center">
              <button onClick={() => test(c.id)} disabled={busyIds.has(c.id) || unavailable}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-semibold transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
                {busyIds.has(c.id) ? "Testing…" : "Test"}
              </button>
              <button onClick={() => { setEditId(c.id); setDisplayName(c.displayName); setAdminKey(""); setExtraKeysText(""); setKeysMode("add"); setScalarValues(scalarValuesFor(scalarFields, c)); setFormOpen(true); }}
                className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
              <button onClick={() => remove(c.id)} disabled={busyIds.has(c.id)}
                className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Remove</button>
            </div>
          )}
        </div>
      ))}

      <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
        <p className="text-xs text-purple-700">{helpText}</p>
        {extraKeys && [].concat(extraKeys.help || []).map((para, i) => (
          <p key={i} className="text-xs text-purple-700 mt-2">{para}</p>
        ))}
      </div>
    </div>
  );
}
